/**
 * Tiny observable for the maintenance flag (kept out of redux so it's never persisted).
 * Checks happen on launch, on foreground, after failed API calls, and every 30 s
 * while maintenance is on.
 */
import { NOT_IN_MAINTENANCE, parseMaintenance, } from './maintenanceState';
let current = NOT_IN_MAINTENANCE;
let url = null;
let inFlight = null;
let lastCheck = 0;
/** When the API last answered with a maintenance 503; a stale flag can't clear it right away. */
let apiReportedAt = 0;
const API_REPORT_GRACE_MS = 60_000;
const listeners = new Set();
const MIN_INTERVAL_MS = 10_000;
/** A hung flag fetch must not block every later check (inFlight is reused). */
const FETCH_TIMEOUT_MS = 5_000;
function emit(next) {
    const changed = next.enabled !== current.enabled ||
        next.message !== current.message ||
        next.until !== current.until ||
        next.startsAt !== current.startsAt;
    current = next;
    if (changed) {
        listeners.forEach(l => l(current));
    }
}
export const maintenanceStore = {
    get: () => current,
    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    setUrl(next) {
        if (next !== url) {
            url = next;
            lastCheck = 0;
            if (!url) {
                emit(NOT_IN_MAINTENANCE);
            }
        }
    },
    /** API said 503 maintenance: show it immediately, then confirm from the flag. */
    report(info) {
        apiReportedAt = Date.now();
        emit(info);
    },
    /**
     * A non-GET API call succeeded, so the server is accepting writes again: clear any
     * maintenance state (including the post-503 grace) right away. Safety net in case
     * the flag poll is held or stuck.
     */
    writeSucceeded() {
        apiReportedAt = 0;
        if (current.enabled) {
            lastCheck = 0;
            emit(NOT_IN_MAINTENANCE);
        }
    },
    async check({ force = false } = {}, fetchImpl = fetch) {
        if (!url) {
            return current;
        }
        if (!force && Date.now() - lastCheck < MIN_INTERVAL_MS) {
            return current;
        }
        if (inFlight) {
            return inFlight;
        }
        lastCheck = Date.now();
        const target = url;
        inFlight = (async () => {
            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const timer = controller
                ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
                : null;
            try {
                // Bust caches: iOS NSURLCache otherwise serves a stale flag without hitting the network.
                const sep = target.includes('?') ? '&' : '?';
                const res = await fetchImpl(`${target}${sep}t=${Date.now()}`, {
                    headers: { Accept: 'application/json' },
                    cache: 'no-store',
                    signal: controller?.signal,
                });
                const next = res.ok
                    ? parseMaintenance(await res.json())
                    : NOT_IN_MAINTENANCE;
                // The API's 503 outranks a flag that may be cached (CDN TTL) for a short while.
                if (!next.enabled &&
                    current.enabled &&
                    Date.now() - apiReportedAt < API_REPORT_GRACE_MS) {
                    return current;
                }
                emit(next);
            }
            catch {
                // Flag host unreachable: fail open, but keep a maintenance state the API
                // reported within the grace window (never indefinitely).
                if (!current.enabled ||
                    Date.now() - apiReportedAt >= API_REPORT_GRACE_MS) {
                    emit(NOT_IN_MAINTENANCE);
                }
            }
            finally {
                if (timer) {
                    clearTimeout(timer);
                }
                inFlight = null;
            }
            return current;
        })();
        return inFlight;
    },
    /** Test helper. */
    reset() {
        current = NOT_IN_MAINTENANCE;
        url = null;
        inFlight = null;
        lastCheck = 0;
        apiReportedAt = 0;
        listeners.clear();
    },
};
